import { useState, useEffect, RefObject, useCallback, useRef } from 'react';

import { normalizeElements } from '../utils';

import useOuterEvent from './useOuterEvent';

/** Hook for properly handling focus state of children components.
 * @example const hasFocus = useFocusWithin([containerRef, ...], (isFocused, element) => { doSomething; });
 * @param onFocusChange Callback function that is invoked with the current focus state and the current element when any child elements takes focus or all of them lose focus.
 * @returns * hasFocus:: A boolean indicating if the ref element has focus or not.
 */
const useFocusWithin = <T extends HTMLElement = HTMLElement>(
  els: (T | null | RefObject<T | null>)[],
  onFocusChange?: (isFocused: boolean, element: T | null) => void
): boolean => {
  const [hasFocus, setFocus] = useState(false);
  const focusedElRef = useRef<T | null>(null);
  const blurTimerRef = useRef<number>();

  const elsRef = useRef(els);
  elsRef.current = els;

  const onBlur = useCallback(
    ({ relatedTarget }: FocusEvent) => {
      if (!hasFocus) return;

      const elements = normalizeElements(elsRef.current);

      if (relatedTarget instanceof Node) {
        // changing focus to another relevant child doesn't count
        const focusedEl = elements.find(el => el?.contains(relatedTarget));
        if (focusedEl) {
          // ... just update the currently focused item
          focusedElRef.current = focusedEl;
          return;
        }
      }

      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = setTimeout(() => {
        if (
          !elements.some(el => {
            return el?.contains(document.activeElement);
          })
        ) {
          setFocus(false);
          onFocusChange?.(false, focusedElRef.current);
          focusedElRef.current = null;
        }
      });
    },
    [hasFocus, onFocusChange]
  );

  const onFocus = useCallback(
    ({ currentTarget }: FocusEvent) => {
      clearTimeout(blurTimerRef.current);

      if (!hasFocus) {
        setFocus(true);
        const targetEl = currentTarget as T;
        onFocusChange?.(true, targetEl);
        focusedElRef.current = targetEl;
      }
    },
    [hasFocus, onFocusChange]
  );

  useOuterEvent(
    'mouseup',
    els,
    useCallback(() => {
      if (hasFocus) {
        setFocus(false);
        onFocusChange?.(false, focusedElRef.current);
        focusedElRef.current = null;
      }
    }, [onFocusChange, hasFocus])
  );

  useEffect(() => {
    const elements = normalizeElements(els);

    elements.forEach(el => {
      el?.addEventListener('focusin', onFocus);
      el?.addEventListener('focusout', onBlur);
    });
    return () => {
      elements.forEach(el => {
        el?.removeEventListener('focusin', onFocus);
        el?.removeEventListener('focusout', onBlur);
      });
    };
  }, [els, onFocus, onBlur]);

  // Clear timeout when component un-mounts.
  useEffect(
    () => () => {
      clearTimeout(blurTimerRef.current);
    },
    []
  );

  return hasFocus;
};

export default useFocusWithin;
